import { CameraRoll } from "@components/camera-roll";
import { CircleButton } from "@components/circle-button";
import { EnhancedScrollArea } from "@components/enhanced-scroll-area";
import { QualityChoiceModal } from "@components/quality-choice-modal";
import { UpgradeToHighQualityButton } from "@components/upgrade-to-high-quality-button";
import { useCameraModes, useImageTransformModes } from "@features/camera/modes";
import { useImages } from "@features/images/use-images";
import { useImportImage } from "@features/images/use-import-image";
import { getLoraPreview, useLoras } from "@features/loras/use-loras";
import { ProjectType } from "@features/projects/type";
import { useProjects } from "@features/projects/use-projects";
import { useUser } from "@features/user/use-user";
import { formatNumber, useNavigateAlt } from "@features/utils";
import {
  ArrowsPointingOutIcon,
  CameraIcon,
  CreditCardIcon,
  FolderIcon,
  HandThumbDownIcon,
  HomeModernIcon,
  PhotoIcon,
  PlusIcon,
  Squares2X2Icon,
  VideoCameraIcon,
} from "@heroicons/react/16/solid";
import {
  HeartFilledIcon,
  LockClosedIcon,
  MagicWandIcon,
  MobileIcon,
  UploadIcon,
} from "@radix-ui/react-icons";
import {
  Badge,
  Button,
  Card,
  Heading,
  Progress,
  Spinner,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import { Page } from "@views/layout/layout";
import { SideBarContainer } from "@views/layout/sidebar";
import _ from "lodash";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";
import { Fragment } from "react/jsx-runtime";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";

export const Home = ({ intro }: { intro?: boolean }) => {
  const { t } = useTranslation();
  const { loras } = useLoras();

  return (
    <Page verticalCenter={false}>
      <div className="w-full md:flex">
        <div className="">
          <HomeSideBar />
        </div>
        <div className="grow space-y-8 min-w-0">
          {intro !== false && !!loras?.data?.length && <HomeIntro />}
          <div>
            <div className="px-4">
              <Heading size="4">{t("camera-roll.title")}</Heading>
            </div>
            <CameraRoll />
          </div>
        </div>
      </div>
    </Page>
  );
};

export const HomeIntro = () => {
  const { t } = useTranslation();
  const { projects } = useProjects();
  const { site } = useParams<{ site?: string }>();
  if (site !== "portraits" && site !== "showroom") {
    return <></>;
  }
  return (
    <div className="px-4 w-full space-y-4">
      <Heading size="4">{t("home.models_sub.models_and_projects")}</Heading>
      <div className="w-full overflow-hidden rounded-sm">
        <EnhancedScrollArea>
          <div className="flex items-start overflow-auto space-x-4">
            <LorasTile />
            {_.orderBy(projects.data || [], "last_image_date").map(
              (project) => (
                <ProjectTile key={project.id} project={project} />
              ),
            )}
          </div>
        </EnhancedScrollArea>
      </div>
    </div>
  );
};

export const LorasTile = () => {
  const { t } = useTranslation();
  const { loras } = useLoras();
  const navigate = useNavigateAlt();
  const { site } = useParams();

  const matchingLoras =
    site === "portraits"
      ? loras.data?.filter(
          (a) => a.subtype === "avatar" || a.subtype === "animal",
        )
      : site === "showroom"
        ? loras.data?.filter(
            (a) => a.subtype === "car" || a.subtype === "object",
          )
        : loras.data;

  if (site === "portraits" || site === "showroom") {
    return (
      <div className="bg-slate-100 dark:bg-slate-800 rounded-lg w-[17rem] md:w-[21rem] h-56 md:h-64 overflow-hidden">
        <Text size="1" className="px-4 py-3 block">
          {t("home.models_sub.recently_created")}
        </Text>
        <div className="flex flex-wrap items-start px-2 ">
          {Array.from(new Array(8)).map((_, i) => {
            const lora = matchingLoras?.filter((a) => a.status !== "failed")?.[
              i
            ];
            if (!lora)
              return (
                <div
                  key={i}
                  className="flex flex-col items-center mx-0 md:mx-2 h-24"
                  style={{
                    opacity: (1 - i / 12) * 0.5,
                  }}
                >
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-slate-200 dark:bg-slate-900 rounded-full" />
                  <div className="w-12 h-4 bg-slate-200 dark:bg-slate-900 rounded-full mt-2 mx-2" />
                </div>
              );
            return (
              <CircleButton
                key={lora.id}
                onClick={(e) =>
                  navigate(
                    `/${site}/camera/new?mode=classic&model=${lora.id}`,
                    e,
                  )
                }
                size="3"
                src={getLoraPreview(lora.id, lora.preview_status)}
                title={lora.name}
                loading={
                  lora.status === "pending" ||
                  lora.preview_status !== "completed"
                }
                tooltip={
                  lora.technology === "images" && lora.subtype === "avatar"
                    ? "Standard, low-likeness model"
                    : undefined
                }
                badge={
                  lora.technology === "images" && lora.subtype === "avatar"
                    ? "LQ"
                    : undefined
                }
              />
            );
          })}
        </div>
      </div>
    );
  }

  return <></>;
};

export const ProjectTile = (props: { project: ProjectType }) => {
  const { images } = useImages("", false, props.project.id);
  const imagesList = images.data || [];
  const { site } = useParams<{ site?: string }>();

  return (
    <Link
      className="bg-slate-100 dark:bg-slate-800 rounded-lg w-48 md:w-96 h-56 md:h-64 overflow-hidden cursor-pointer hover:opacity-90"
      to={`/${site}/project/${props.project.id}`}
    >
      <Text size="1" className="px-4 py-3 block">
        {props.project.name || "Untitled Project"}
      </Text>
      <div className="flex flex-wrap">
        {Array.from(new Array(8)).map((_, i) =>
          imagesList[i]?.status === "failed" ? (
            <Fragment key={i} />
          ) : imagesList[i]?.status === "pending" ? (
            <div
              className="w-1/2 md:w-1/4 bg-slate-200 dark:bg-slate-500 flex items-center justify-center"
              style={{
                aspectRatio: "3 / 4",
              }}
            >
              <Spinner key={imagesList[i]?.id || i} className="w-2 h-2" />
            </div>
          ) : (
            <img
              key={imagesList[i]?.id || i}
              src={
                imagesList[i]?.url ||
                "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
              }
              alt={"Image " + (i + 1)}
              className="w-1/2 md:w-1/4 object-cover bg-slate-200 dark:bg-slate-500"
              style={{
                opacity: imagesList[i] ? 1 : 1 - i / 12,
                aspectRatio: "3 / 4",
              }}
            />
          ),
        )}
      </div>
    </Link>
  );
};

export const HomeSideBar = () => {
  const { t } = useTranslation();
  const { modes } = useCameraModes();
  const imageModes = useImageTransformModes();
  const { pathname } = useLocation();
  const { id, site } = useParams<{ id?: string; site: string }>();
  const defaultProjectId = pathname.startsWith("/project/") ? id : "none";
  const { loras } = useLoras();
  const navigate = useNavigateAlt();
  const [showQualityModal, setShowQualityModal] = useState(false);

  const isLocked = !loras.data?.length;
  const handleLockedClick = () => setShowQualityModal(true);

  // Find the first low-quality avatar model for upgrade path
  const lowQualityAvatar = loras.data?.find(
    (lora) =>
      lora.technology === "images" &&
      lora.subtype === "avatar" &&
      lora.status === "completed",
  );

  const handleAddMorePictures = () => {
    navigate(
      lowQualityAvatar?.id
        ? `/${site || "portraits"}/lora/${lowQualityAvatar?.id}/upgrade`
        : `/${site || "portraits"}/lora/model`,
    );
    setShowQualityModal(false);
  };

  const handleUpgrade = () => {
    navigate(`/billing`);
    setShowQualityModal(false);
  };

  return (
    <SideBarContainer>
      <div className="h-4 hidden md:block" />

      <UpgradeToHighQualityButton className={"mb-4"} />

      <Link
        to={loras?.data?.length ? `/${site}/templates` : `/upload?free=1`}
        className="w-full hidden md:block"
      >
        <Button variant="solid" className="w-full" size="3">
          <CameraIcon className="h-4 w-4 mr-1" />
          {t("home.take_photo")}
        </Button>
      </Link>
      <div className="hidden md:block">
        <ImportButton projectId={defaultProjectId || "none"} />
      </div>

      <div className="h-4 hidden md:block" />
      <div className="md:block hidden">
        <Heading size="1" className="!mb-1">
          {t("home.home")}
        </Heading>
        <ButtonLink
          href={`/${site}/home`}
          icon={(p) => <Squares2X2Icon {...p} />}
          exactPath
        >
          {t("home.all_pictures")}
        </ButtonLink>
        <ButtonLink
          href={`/${site}/home/search/favorite:1`}
          icon={(p) => <HeartFilledIcon {...p} />}
        >
          {t("home.favorites")}
        </ButtonLink>
        <ButtonLink
          href={`/${site}/home/search/subtype:upscale`}
          icon={(p) => <ArrowsPointingOutIcon {...p} />}
          exactPath
        >
          {t("home.upscaled")}
        </ButtonLink>
        <ButtonLink
          href={`/${site}/home/search/subtype:video`}
          icon={(p) => <CameraIcon {...p} />}
          exactPath
        >
          {t("home.videos", "Videos")}
        </ButtonLink>
        <ButtonLink
          href={`/${site}/home/search/subtype:import`}
          icon={(p) => <UploadIcon {...p} />}
          exactPath
        >
          {t("home.imported")}
        </ButtonLink>
        <ButtonLink
          href={`/${site}/trash`}
          icon={(p) => <HandThumbDownIcon {...p} />}
        >
          {t("home.rejects")}
        </ButtonLink>
        <ProjectMenu />
        <AvatarMenu />
        <div className="h-4" />
        <Heading size="1" className="!mb-1">
          {t("home.tools")}
        </Heading>
        <ButtonLink
          href={`/${site}/templates?mode=pack&project=${defaultProjectId}`}
          icon={(p) => <modes.classic.icon {...p} />}
          locked={isLocked}
          onLockedClick={handleLockedClick}
        >
          {modes.classic.label}
        </ButtonLink>
        <ButtonLink
          href={`/${site}/templates?mode=image&project=${defaultProjectId}`}
          icon={(p) => <imageModes.reimagine.icon {...p} />}
          locked={isLocked}
          onLockedClick={handleLockedClick}
        >
          {imageModes.reimagine.label}
        </ButtonLink>
        {site === "portraits" && (
          <>
            <ButtonLink
              href={`/${site}/templates?mode=image&project=${defaultProjectId}`}
              icon={(p) => <imageModes.faceswap.icon {...p} />}
              locked={isLocked}
              onLockedClick={handleLockedClick}
            >
              {imageModes.faceswap.label}
            </ButtonLink>
            <ButtonLink
              href={`/${site}/templates?mode=tryon&project=${defaultProjectId}`}
              icon={(p) => <modes.tryon.icon {...p} />}
              locked={isLocked}
              onLockedClick={handleLockedClick}
            >
              {modes.tryon.label}
            </ButtonLink>
            <ButtonLink
              href={`/${site}/templates?mode=haircut&project=${defaultProjectId}`}
              icon={(p) => <modes.haircut.icon {...p} />}
              locked={isLocked}
              onLockedClick={handleLockedClick}
            >
              {modes.haircut.label}
            </ButtonLink>
            <ButtonLink
              href={`/${site}/templates?mode=pack&project=${defaultProjectId}`}
              icon={(p) => <modes.pack.icon {...p} />}
              locked={isLocked}
              onLockedClick={handleLockedClick}
            >
              {modes.pack.label}
            </ButtonLink>
          </>
        )}
        {false && (
          <>
            <Button
              variant="ghost"
              color="gray"
              className="w-full justify-start"
            >
              <HomeModernIcon className="h-4 w-4 mr-1" />
              Architecte d'intérieur
            </Button>
            <Button
              variant="ghost"
              color="gray"
              className="w-full justify-start"
            >
              <PhotoIcon className="h-4 w-4 mr-1" />
              Photographie libre
            </Button>
            <Button
              variant="ghost"
              color="gray"
              className="w-full justify-start"
            >
              <MagicWandIcon className="h-4 w-4 mr-1" />
              Éditer une photo
            </Button>
            <Button
              variant="ghost"
              color="gray"
              className="w-full justify-start"
            >
              <VideoCameraIcon className="h-4 w-4 mr-1" />
              Vidéos
            </Button>
          </>
        )}
        <div className="grow" />
        <div className="h-4" />
        <Heading size="1" className="!mb-1">
          {t("home.account")}
        </Heading>
        <ButtonLink href="/billing" icon={(p) => <CreditCardIcon {...p} />}>
          {t("home.billing")}
        </ButtonLink>
        {false && (
          <Button variant="ghost" color="gray" className="w-full justify-start">
            <MobileIcon className="h-4 w-4 mr-1 !text-red-500 hue-animation" />
            Application mobile
            <Badge className="ml-1" color="yellow">
              New
            </Badge>
          </Button>
        )}
        <div className="h-1" />
        <CreditBlock />
      </div>
      <div className="h-4" />

      <QualityChoiceModal
        isOpen={showQualityModal}
        onClose={() => setShowQualityModal(false)}
        onContinueStandard={() => setShowQualityModal(false)}
        onAddMorePictures={handleAddMorePictures}
        onUpgrade={handleUpgrade}
      />
    </SideBarContainer>
  );
};

const ProjectMenu = () => {
  const { t } = useTranslation();
  const { projects } = useProjects();
  const { site } = useParams<{ site?: string }>();
  return (
    <>
      <div className="h-4" />
      <Heading size="1" className="!mb-1">
        {t("home.projects")}
      </Heading>
      {_.sortBy(projects.data || [], "name").map((project) => (
        <ButtonLink
          key={project.id}
          href={`/${site}/project/${project.id}`}
          icon={(p) => <FolderIcon {...p} />}
        >
          {project.name}
        </ButtonLink>
      ))}
      <ButtonLink
        href={`/${site}/project/new/form`}
        icon={(p) => <PlusIcon {...p} />}
      >
        {t("home.new_project")}
      </ButtonLink>
    </>
  );
};

const AvatarMenu = () => {
  const { t } = useTranslation();
  const { loras } = useLoras();
  const { pathname } = useLocation();
  const { id } = useParams<{ id?: string }>();
  const { site } = useParams<{ site?: string }>();
  const defaultProjectId = pathname.startsWith("/project/") ? id : "none";

  const groupedLoras = _.groupBy(
    _.sortBy(loras.data || [], "name").filter(
      (a) => a.type === "model" && a.status !== "failed",
    ),
    (lora) => {
      if (lora.subtype === "car") return "cars";
      if (lora.subtype === "object") return "objects";
      return "avatars";
    },
  );

  if (site === "portraits") {
    delete groupedLoras.cars;
    delete groupedLoras.objects;
  }

  if (site === "showroom") {
    delete groupedLoras.avatars;
  }

  if (site === "architect") {
    return <></>;
  }

  return (
    <>
      <div className="h-4" />
      {Object.keys(groupedLoras).map((a, i) => (
        <>
          <Heading size="1" className={i > 0 ? "!mt-1 !mb-1" : "!mb-1"} key={a}>
            {
              {
                avatars: t("home.avatars"),
                cars: t("home.cars"),
                objects: t("home.others"),
              }[a]
            }
          </Heading>
          {groupedLoras[a].map((lora) => (
            <ButtonLink
              key={lora.id}
              disabled={lora.status !== "completed"}
              href={`/${site}/camera/new?model=${lora.id}&project=${defaultProjectId}`}
              icon={() =>
                lora.status !== "completed" ? (
                  <Spinner className="mr-1" />
                ) : (
                  <div
                    className="h-5 w-5 rounded-full bg-slate-500 -ml-0.5 mr-0.5"
                    style={{
                      backgroundPosition: "center",
                      backgroundSize: "cover",
                      backgroundImage: `url(${getLoraPreview(lora.id)})`,
                    }}
                  />
                )
              }
            >
              {lora.name}{" "}
              {lora.technology === "images" && lora.subtype === "avatar" && (
                <Tooltip content="Standard, low-likeness model">
                  <i>(LQ)</i>
                </Tooltip>
              )}
            </ButtonLink>
          ))}
        </>
      ))}
      {!Object.keys(groupedLoras).length && (
        <Heading size="1" className={"!mb-1"}>
          {t("home.models")}
        </Heading>
      )}
      <ButtonLink
        href={`/${site || "portraits"}/lora/model`}
        icon={(p) => <PlusIcon {...p} />}
      >
        {t("home.new_model")}
      </ButtonLink>
    </>
  );
};

export const ButtonLink = (props: {
  href: string;
  icon: (p: { className?: string }) => React.ReactNode;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  exactPath?: boolean;
  locked?: boolean;
  onLockedClick?: () => void;
}) => {
  const { pathname } = useLocation();
  const isActive = props.exactPath
    ? pathname === props.href.split("?")[0]
    : pathname.startsWith(props.href.split("?")[0]);

  if (props.locked) {
    return (
      <div className="block py-1">
        <Button
          variant="ghost"
          color="gray"
          className={twMerge(
            "w-full justify-start opacity-50",
            props.className,
          )}
          onClick={props.onLockedClick}
        >
          {props.icon && props.icon({ className: "h-4 w-4 mr-1" })}
          {props.children}
          <LockClosedIcon className="h-3 w-3 ml-auto text-slate-400" />
        </Button>
      </div>
    );
  }

  return (
    <Link to={props.href} className="block py-1">
      <Button
        variant="ghost"
        color="gray"
        disabled={props.disabled}
        className={twMerge(
          "w-full justify-start",
          isActive && "bg-slate-200 dark:bg-slate-700",
          props.className,
        )}
      >
        {props.icon && props.icon({ className: "h-4 w-4 mr-1" })}
        {props.children}
      </Button>
    </Link>
  );
};

export const CreditBlock = () => {
  const { user } = useUser();
  return (
    <Card>
      <Progress
        value={
          Math.min(
            1,
            ((user.data?.credits || 0) + (user.data?.additional_credits || 0)) /
              ((user.data?.plan_credits || 0) +
                (user.data?.additional_credits || 0)),
          ) * 100
        }
        color={
          (user.data?.credits || 0) + (user.data?.additional_credits || 0) < 100
            ? "red"
            : undefined
        }
      />
      <div className="mt-1 -mb-1 block">
        <Text size="2">
          {formatNumber(
            (user.data?.credits || 0) + (user.data?.additional_credits || 0),
          )}{" "}
          credits over{" "}
          {formatNumber(
            (user.data?.plan_credits || 0) +
              (user.data?.additional_credits || 0),
          )}
        </Text>
        {!!user.data?.plan_renew_at && (
          <Text size="1" className="block text-slate-500">
            {`Renew on ${new Date(
              user.data.plan_renew_at,
            ).toLocaleDateString()}`}
          </Text>
        )}
      </div>
    </Card>
  );
};

// Import Button Component
const ImportButton = ({ projectId }: { projectId: string }) => {
  const { t } = useTranslation();
  const { importImage, loading } = useImportImage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error("File size must be less than 10MB");
      return;
    }

    try {
      await importImage(file, projectId !== "none" ? projectId : undefined);
      toast.success("Image imported successfully!");

      // Clear the input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Failed to import image");
    }
  };

  return (
    <div className="w-full relative pr-4">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
      <Button
        variant="ghost"
        className="my-2 mx-0 w-full"
        size="2"
        onClick={() => fileInputRef.current?.click()}
        disabled={loading}
      >
        {loading ? (
          <Spinner className="h-4 w-4 mr-1" />
        ) : (
          <UploadIcon className="h-4 w-4 mr-1" />
        )}
        {loading ? t("home.importing") : t("home.import_picture")}
      </Button>
    </div>
  );
};
